import { inject, injectable } from 'inversify';
import { types } from 'mediasoup-client';
import { DataConsumer } from 'mediasoup-client/lib/DataConsumer';
import { serializeError } from 'serialize-error';
import { CHANNEL_MESSAGES_DATA_PRODUCER } from '../../constants/common';
import { TOKEN } from '../../inversify.tokens';
import { CreateDataConsumerPayload } from '../../types/common';
import { CallState } from '../CallState';
import ChannelAudioObserverEventHandler from '../handlers/ChannelAudioObserverEventHandler';
import Logger from '../Logger';
import { MyPeer } from '../MyPeer';
import type { PeerDataProducerLabels } from '../signalingApiClient/api';
import SignalingApiClient from '../signalingApiClient/SignalingApiClient';

@injectable()
class DataChannelsManager {
  readonly #logger: Logger;

  #audioObserver?: DataConsumer;

  #channelMessagesDataConsumer?: DataConsumer;

  constructor(
    @inject(TOKEN.CallState) private readonly state: CallState,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
    @inject(TOKEN.MyPeer) private readonly myPeer: MyPeer,
    @inject(TOKEN.SignalingApiClient) private readonly signalingApiClient: SignalingApiClient,
    @inject(TOKEN.ChannelAudioObserverEventHandler)
    private readonly channelAudioObserverEventHandler: ChannelAudioObserverEventHandler,
  ) {
    this.#logger = loggerFactory('DataChannels');
  }

  get channelMessagesDataConsumer(): DataConsumer | undefined {
    return this.#channelMessagesDataConsumer;
  }

  async createAudioObserver(transport: types.Transport): Promise<void> {
    const logCtx = {
      channelId: this.state.channelId,
      appId: this.state.appId,
      peerId: this.myPeer?.id,
    };

    try {
      const { producerId } = await this.getAudioObserverProducer() as { producerId: string };
      this.#audioObserver = await this.createDataConsumer({
        producerId,
        appId: this.state.appId,
        channelId: this.state.channelId,
        transport,
      });

      const boundHandle = this.channelAudioObserverEventHandler.handle.bind(this.channelAudioObserverEventHandler);

      const onClose = (): void => {
        this.#audioObserver?.removeListener('message', boundHandle);
        this.#audioObserver?.removeListener('close', onClose);
      };

      this.#audioObserver.on('close', onClose);
      this.#audioObserver.on('message', boundHandle);

      this.#logger.debug('Successfully create audio observer', logCtx);
    } catch (error) {
      this.#logger.error('Failed to create audio observer', {
        ...logCtx,
        error: serializeError(error),
      });
    }
  }

  async createChannelMessagesDataConsumer(transport: types.Transport): Promise<void> {
    const logCtx = {
      channelId: this.state.channelId,
      appId: this.state.appId,
      peerId: this.myPeer?.id,
    };

    try {
      const { id: producerId } = await this.createDataProducer(
        transport.id,
        CHANNEL_MESSAGES_DATA_PRODUCER,
      );

      this.#channelMessagesDataConsumer = await this.createDataConsumer({
        producerId,
        appId: this.state.appId,
        channelId: this.state.channelId,
        ordered: true,
        transport,
      });

      this.#logger.debug('Successfully created channel messages data consumer', {
        ...logCtx,
        producerId,
      });
    } catch (error) {
      this.#logger.error('Failed to create channel messages data consumer', {
        ...logCtx,
        error,
      });

      throw error;
    }
  }

  closeAudioObserver(): void {
    this.#audioObserver?.close();
  }

  closeChannelMessagesDataConsumer(): void {
    this.#channelMessagesDataConsumer?.close();
  }

  private async createDataProducer(transportId: string, label: string): Promise<{ id: string }> {
    const { data } = await this.signalingApiClient.transport.produceDataToTransport(transportId, {
      label: label as PeerDataProducerLabels,
    });

    return data;
  }

  private async createDataConsumer({
    producerId,
    appId,
    channelId,
    ordered,
    transport,
  }: CreateDataConsumerPayload): Promise<types.DataConsumer> {
    if (!transport) {
      this.#logger.error('createDataConsumer()', { reason: 'missing receive transport' });
      throw new Error('Failed to create data consumer due to missing receive transport');
    }

    this.#logger.debug('createDataConsumer()', { producerId });
    const response = await this.signalingApiClient.dataConsumer.createDataConsumer({
      ordered,
      dataProducerId: producerId,
      transportId: transport.id,
      appData: {
        peerId: this.myPeer.id,
        producerId,
        appId,
        channelId,
      },
    });

    const options = response.data as unknown as types.DataConsumerOptions;

    const consumer = await transport.consumeData(options);
    this.#logger.debug('Data consumer created', { consumerId: consumer.id });

    return consumer;
  }

  private async getAudioObserverProducer(): Promise<{ producerId: string }> {
    const { data } = await this.signalingApiClient.producer.getAudioObserverProducer();

    return data;
  }
}

export default DataChannelsManager;
